import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Planet as Satellite, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SatellitePanel({ project, reload, catalog }) {
  const id = project.id;
  const [provider, setProvider] = useState(project.preferred_satellite || "sentinel-2");
  const [from, setFrom] = useState(project.date_from || "2024-06-01");
  const [to, setTo] = useState(project.date_to || "2024-09-30");
  const [cloud, setCloud] = useState(project.max_cloud || 20);
  const [loading, setLoading] = useState(false);
  const scenes = project.scenes || [];
  const selectedId = project.selected_scene?.scene_id;

  const search = async () => {
    if (!project.boundary_metrics) return toast.error("Set a boundary before searching imagery");
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${id}/satellite/search`, {
        provider, date_from: from, date_to: to, max_cloud: Number(cloud),
      });
      toast.success(`${data.count} scenes found`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const select = async (scene) => {
    try { await api.post(`/projects/${id}/satellite/select`, { scene }); toast.success("Scene selected"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div><Label className="text-xs">Satellite / Sensor</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="h-9 mt-1" data-testid="sat-provider"><SelectValue /></SelectTrigger>
            <SelectContent>{(catalog?.satellite_providers || []).filter((p) => !/landsat/i.test((p.key || "") + (p.name || ""))).map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Date From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 mt-1" data-testid="sat-from" /></div>
          <div><Label className="text-xs">Date To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 mt-1" data-testid="sat-to" /></div>
        </div>
        <div><Label className="text-xs">Max Cloud Cover: {cloud}%</Label>
          <Input type="range" min="0" max="100" value={cloud} onChange={(e) => setCloud(e.target.value)} className="mt-1" data-testid="sat-cloud" /></div>
        <Button className="w-full" onClick={search} disabled={loading} data-testid="sat-search">
          <Satellite size={16} className="mr-1" />{loading ? "Searching real archives…" : "Search Online Imagery"}
        </Button>
      </div>

      <div className="space-y-2">
        {scenes.length > 0 && <p className="overline">{scenes.length} scenes · best first</p>}
        {scenes.map((s) => {
          const sel = s.scene_id === selectedId;
          return (
            <div key={s.scene_id} data-testid={`scene-${s.scene_id}`}
              className={`flex gap-3 p-2.5 rounded-md border transition-colors ${sel ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
              <div className="w-16 h-16 rounded bg-muted overflow-hidden shrink-0">
                {s.thumbnail ? <img src={s.thumbnail} alt="preview" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[11px] truncate">{s.scene_id}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="secondary" className="text-[10px]">{(s.datetime || "").slice(0, 10)}</Badge>
                  <Badge variant="outline" className="text-[10px]">☁ {s.cloud_cover ?? "-"}%</Badge>
                  <Badge variant="outline" className="text-[10px]">Q {s.quality_score}</Badge>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">{s.platform} · coverage {s.coverage_pct}% · {Object.keys(s.assets || {}).length} bands</div>
              </div>
              <Button size="sm" variant={sel ? "secondary" : "outline"} className="self-center h-8" onClick={() => select(s)} data-testid={`select-scene-${s.scene_id}`}>
                {sel ? <CheckCircle size={16} weight="fill" className="text-primary" /> : "Select"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
